<script setup lang="ts">
//#region import
import { ref, computed, watch } from 'vue';
//#endregion

//#region types
interface Props {
  page: number;
  pageSize: number;
  count: string | number,
  showSizeSelect?: boolean,
}

interface Emits {
  'page-change': [currentPage: number, currentPageSize: number],
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  showSizeSelect: false,
});

const emit = defineEmits<Emits>();
//#endregion

//#region state
const currentPage = ref(props.page);
const currentPageInp = ref(props.page.toString());
const currentPageSize = ref(props.pageSize);
//#endregion

//#region computed
const pageSizes = computed(() => [
  { id: 10, name: 10 },
  { id: 20, name: 20 },
  { id: 30, name: 30 },
  { id: 50, name: 50 },
  { id: 100, name: 100 },
  { id: 250, name: 250 },
]);

const totalPages = computed(() => Math.ceil(Number(props.count) / props.pageSize));
//#endregion

//#region methods
const nextPage = () => {
  return currentPage.value < totalPages.value ? currentPage.value += 1 : '';
};

const prevPage = () => {
  return currentPage.value > 1 ? currentPage.value = 1 : '';
};
//#endregion

//#region watch
watch(() => props.count, () => {
  if (currentPage.value > totalPages.value) {
    currentPage.value = 1;
  }
});

watch(() => props.page, (nv) => {
  if (nv !== currentPage.value) {
    currentPage.value = nv;
  }
});

watch(() => props.pageSize, (nv) => {
  currentPageSize.value = nv;
});

watch(currentPage, (nv) => {
  if (!nv || nv < 1 || nv > totalPages.value) {
    currentPage.value = 1;
    return;
  }
  currentPageInp.value = currentPage.value.toString();
  emit('page-change', currentPage.value, currentPageSize.value);
});

watch(currentPageInp, (nv) => {
  const v = parseInt(nv.replace(/[^0-9]/g, ''), 10);
  if (!Number.isNaN(v) && v !== currentPage.value) {
    currentPage.value = v;
  }
});

watch(currentPageSize, (nv) => {
  const newSize = typeof nv === 'string' ? parseInt(nv, 10) : nv;
  currentPageSize.value = newSize;

  if (Math.ceil(Number(props.count) / newSize) < currentPage.value) {
    currentPage.value = 1;
    return;
  }
  emit('page-change', currentPage.value, newSize);
});
//#endregion
</script>

<template>
  <div class="ui-pagination">
    <UiSelectElement
      v-if="showSizeSelect"
      v-model="currentPageSize"
      prepend-icon="bars"
      class="t:mr-12"
      :list="pageSizes"
      width="80"
    />
    <UiIcon
      :class="[
        'ui-pagination__icon',
        { 'ui-pagination__icon_disabled': currentPage <= 1 }
      ]"
      :size="16"
      name="angle-double-left"
      substyle="far"
      color="var(--btn-color-main)"
      @click="prevPage"
    />
    <UiIcon
      :class="[
        'ui-pagination__icon',
        { 'ui-pagination__icon_disabled': currentPage <= 1 }
      ]"
      :size="16"
      name="chevron-left"
      substyle="far"
      color="var(--btn-color-main)"
      class="t:mx-10"
      @click="currentPage > 1 ? currentPage -= 1 : ''"
    />
    <div class="ui-pagination__pages">
      {{ $t('reports.payments.page') }}
      <UiInput
        ref="input"
        v-model="currentPageInp"
        width="46px"
        type="text"
        class="ui-pagination__pages-input"
      />
      {{ $t('reports.payments.page_of') }} {{ totalPages === 0 ? 1 : totalPages }}
    </div>
    <UiIcon
      :class="[
        'ui-pagination__icon',
        { 'ui-pagination__icon_disabled': currentPage >= totalPages }
      ]"
      :size="16"
      name="chevron-right"
      substyle="far"
      color="var(--btn-color-main)"
      class="t:mx-10"
      @click="nextPage"
    />
    <UiIcon
      :class="[
        'ui-pagination__icon',
        { 'ui-pagination__icon_disabled': currentPage >= totalPages }
      ]"
      :size="16"
      name="angle-double-right"
      substyle="far"
      color="var(--btn-color-main)"
      @click="currentPage < totalPages ? currentPage = totalPages : ''"
    />
  </div>
</template>

<style lang="scss">
.ui-pagination{
  display: flex;
  align-items: center;
  &__pages {
    display: flex;
    align-items: center;
    font-size: 14px;
    white-space: nowrap
  }
  &__pages-input {
    margin: 0 4px;
    .ui-input__element{
      text-align: center;
    }
  }
  &__icon {
    cursor: pointer;
    &_disabled {
      filter: grayscale(100%);
      opacity: .5;
      cursor: not-allowed;
    }
  }
}
</style>
